import React from 'react';
import { Button, Chip, Container, Divider, Icon, Paper, Tab, Tabs } from '@mui/material';
import { V4_BUTTON_DEFAULT } from '../../theme/v4Compat';
import { Title, Loader, LegacyGrid as Grid } from '../../elements';
import useAuthContext from '../../hooks/useAuthContext';
import { notifications, extractApiErrorMessage } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteIcon from '@mui/icons-material/Delete';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import {
  InjectionHistoryView,
  InjectionHistoryViewHandle,
  UpdateLocationModal,
  LocationSupplyFeedModal,
  LocationHistory,
  VaccinationAndMedication,
  LocationVaccinationAndMedication,
  LocationFeedTab,
  LocationDetails
} from '../../components';
import { LoaderSize } from './../../constants/constants';
import { LocationSupplyFeedModalResult } from '../../components/LocationSupplyFeedModal/component';
import { InjectionsService, ApiService } from '../../hooks';
import { useTranslation } from 'react-i18next';
import LocationViewAnimals from '../../components/LocationViewAnimals';
import { LocationAnimal } from '../../models/locations';
import { useParams, useNavigate } from 'react-router-dom';
import UserRole from '../../constants/user-role';
import {
  HealthSystemDomainLocationsResultLocationViewModel,
  HealthSystemDomainLocationsCommandsUpdateLocationCommand
} from '../../api/HealthSystemApi';

const LocationView: React.FC<any> = () => {
  const apiService = new ApiService();
  const injectionsService = new InjectionsService();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();

  const tabValues: { [key: string]: string } = {
    overview: 'overview',
    history: 'history',
    feed: 'feed'
  };

  const getTabIndex = (): string => {
    return tab && tabValues[tab] ? tabValues[tab] : tabValues.overview;
  };

  const [currentTab, setCurrentTab] = React.useState(getTabIndex());

  React.useEffect(() => {
    setCurrentTab(getTabIndex());
  }, [tab]);

  const handleTabChange = (_event: React.ChangeEvent<any>, newValue: string) => {
    setCurrentTab(newValue);
    navigate(`/locations/${id}/${newValue}`);
  };

  const [location, setLocation] = React.useState<HealthSystemDomainLocationsResultLocationViewModel | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);

  const loadLocation = async () => {
    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('Location ID is undefined.');
      return;
    }

    await apiService.api.locations
      .locationsViewLocation({ Id: id })
      .then((location) => {
        setLocation(location.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        setLoaded(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadLocation();
      loadData();
    }
  }, [loaded]);

  const [updateDialogOpen, setUpdateDialogOpen] = React.useState(false);

  const onUpdateLocation = (location: HealthSystemDomainLocationsResultLocationViewModel) => {
    setUpdateDialogOpen(false);

    if (location == null) {
      return;
    }

    apiService.api.locations
      .locationsUpdateLocation(buildUpdateLocationCommand())
      .then(() => {
        notifications.success(t('f-loc-upd-ok'));
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const buildUpdateLocationCommand = (): HealthSystemDomainLocationsCommandsUpdateLocationCommand => {
    if (!location) {
      throw new Error('Location must be loaded before building the update command');
    }

    return {
      id: location.id,
      animalType: location.animalType,
      area: location.area,
      metadata: location.metadata,
      isSoftDeleted: location.isSoftDeleted,
      number: location.number ?? null,
      pen: location.pen ?? null,
      positioning: location.positioning,
      room: location.room ?? null,
      stable: location.stable
    };
  };

  // operational strategy visibility
  const [locationAnimals, setLocationAnimals] = React.useState<LocationAnimal[]>([]);
  const [animalsLoaded, setAnimalsLoaded] = React.useState(false);
  const [hasLocationInjections, setHasLocationInjections] = React.useState(false);
  const hasChippedAnimals = locationAnimals.length > 0;
  const showPigletsSection = !animalsLoaded || locationAnimals.length > 0;
  // * operational strategy visibility

  // * location cycle modal

  // medical supply oral feed modal
  const [supplyFeedDialogOpen, setSupplyFeedDialogOpen] = React.useState(false);
  const injectionHistoryRef = React.useRef<InjectionHistoryViewHandle>(null!);

  const handleFeedMedicalSupply = (result: LocationSupplyFeedModalResult | null) => {
    setSupplyFeedDialogOpen(false);

    if (result === null) {
      return;
    }

    if (!id) {
      notifications.error('Location ID is undefined.');
      return;
    }

    injectionsService
      .createOralFeedInjection(result.farmSupplyId, [id], result.repeatDays, result.dose)
      .then(() => {
        notifications.success(t('f-loc-feed-ok'));
        reloadInjectionHistory();
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  // using React.ref to trigger child reload
  const reloadInjectionHistory = () => {
    injectionHistoryRef.current.reload();
  };
  // * medical supply oral feed modal

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid container spacing={2}>
          {/* Tabs section */}
          <Grid item xs={12}>
            <Grid container direction="column">
              <Tabs value={currentTab} onChange={handleTabChange} scrollButtons="auto">
                <Tab label={t('f-loc-view-overview')} value="overview" />
                <Tab label={t('f-loc-view-history')} value="history" />
                <Tab label={t('f-feed-reg-tab')} value="feed" />
              </Tabs>
              <Divider />
            </Grid>
          </Grid>

          {/* Overview Tab Content */}
          {currentTab === tabValues.overview && (
            <>
              {/* location view section */}
              <Grid item xs={12}>
                <Paper
                  sx={{ p: 2, display: 'flex', overflow: 'auto', flexDirection: 'column', minHeight: 200 }}
                  elevation={1}
                >
                  <Grid container spacing={1}>
                    {/* location overview header */}
                    <Grid item container direction="row" sx={{ justifyContent: 'space-between' }}>
                      <Grid item container md={6} xs={6} spacing={1}>
                        <Grid item>
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-brand" size="2x" />
                        </Grid>
                        <Grid item>
                          <Title>{t('f-loc-view-title')}</Title>
                        </Grid>
                      </Grid>

                      {/* action buttons */}
                      <Grid item>
                        <Button
                          id="feedSupplyBtn"
                          sx={{ ...V4_BUTTON_DEFAULT, m: 1 }}
                          color="inherit"
                          size="small"
                          variant="contained"
                          startIcon={<Icon>{'bubble_chart'}</Icon>}
                          onClick={() => setSupplyFeedDialogOpen(true)}
                        >
                          {t('f-loc-view-medication-supply')}
                        </Button>

                        {(state.role === UserRole.manager || state.role === UserRole.admin) && (
                          <Button
                            id="editLocationBtn"
                            size="small"
                            sx={{ ...V4_BUTTON_DEFAULT, m: 1 }}
                            color="inherit"
                            variant="contained"
                            startIcon={<Icon>{'edit'}</Icon>}
                            onClick={() => setUpdateDialogOpen(true)}
                          >
                            {t('f-com-edit')}
                          </Button>
                        )}
                      </Grid>
                    </Grid>

                    {/* location data section */}
                    {loaded && location && (
                      <Grid item sm={8} xs={12} id="locationFields">
                        <Grid container spacing={1}>
                          <Grid item md={12}>
                            {location.isSoftDeleted && (
                              <Chip
                                sx={{ m: 1 }}
                                size="small"
                                avatar={<DeleteIcon />}
                                label={t('f-loc-upd-del')}
                                variant="outlined"
                              />
                            )}
                          </Grid>

                          <Grid item lg={7} md={8} xs={10}>
                            <LocationDetails location={location} />
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                    {/* location data section */}
                    <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
                  </Grid>
                </Paper>
              </Grid>
              {/* location view section */}

              {/* injection history section */}
              <Grid item xs={12}>
                <InjectionHistoryView entityId={id as string} ref={injectionHistoryRef} />
              </Grid>
              {/* injection history section */}

              {showPigletsSection && (
                <Grid item xs={12}>
                  <LocationViewAnimals
                    locationId={id as string}
                    onAnimalsLoaded={(animals) => {
                      setLocationAnimals(animals);
                      setAnimalsLoaded(true);
                    }}
                  />
                </Grid>
              )}

              {/* Operational Strategy: animal-level analytics when chipped animals exist */}
              {hasChippedAnimals && loaded && location && (
                <Grid item xs={12}>
                  <VaccinationAndMedication animalType={location.animalType} locationId={id} hideLocationPicker />
                </Grid>
              )}

              {/* Operational Strategy: location-level analytics when no chipped animals */}
              {!hasChippedAnimals && (
                <Grid item xs={12} style={{ display: hasLocationInjections ? undefined : 'none' }}>
                  <LocationVaccinationAndMedication locationId={id as string} onHasData={setHasLocationInjections} />
                </Grid>
              )}
            </>
          )}

          {/* History Tab Content */}
          {currentTab === tabValues.history && (
            <>
              {loaded && location && (
                <Grid item xs={12}>
                  <LocationDetails location={location} variant="compact" />
                </Grid>
              )}
              <Grid item xs={12}>
                <LocationHistory locationId={id as string} />
              </Grid>
            </>
          )}

          {/* Feed Tab Content */}
          {currentTab === tabValues.feed && (
            <>
              {loaded && location && (
                <Grid item xs={12}>
                  <LocationDetails location={location} variant="compact" />
                </Grid>
              )}
              <Grid item xs={12}>
                <LocationFeedTab locationId={id as string} />
              </Grid>
            </>
          )}
        </Grid>
      </Container>

      {location && (
        <UpdateLocationModal
          location={location}
          opened={updateDialogOpen}
          showLastLocationMessage={false}
          onLocationUpdated={(location: HealthSystemDomainLocationsResultLocationViewModel) =>
            onUpdateLocation(location)
          }
        />
      )}

      <LocationSupplyFeedModal opened={supplyFeedDialogOpen} onModalAction={handleFeedMedicalSupply} />
    </>
  );
};

export default LocationView;
